import { ShieldCheck, UserCheck, Sparkles, Paintbrush, ClipboardCheck, ArrowUpRight } from 'lucide-react';
import { Reveal, SectionHeading } from './Reveal';

const SERVICES = [
  {
    title: 'Guaranteed Rent',
    desc: 'You receive fixed rental payments every month, regardless of occupancy. No voids, no arrears, no chasing.',
    span: 'md:col-span-7',
    Icon: ShieldCheck,
  },
  {
    title: 'Trusted Tenants & Guests',
    desc: 'We house vetted professionals, corporate clients, families on vacation and trusted guests who value high-quality accommodation.',
    span: 'md:col-span-5',
    Icon: UserCheck,
  },
  {
    title: 'Meticulous Property Care',
    desc: 'Your property is maintained to an immaculate standard, with frequent professional cleaning and inspections.',
    span: 'md:col-span-5',
    Icon: Sparkles,
  },
  {
    title: 'Premium Uplift',
    desc: 'We invest in minor refurbishments, stylish furnishings and decor upgrades to keep your property attractive and well-maintained.',
    span: 'md:col-span-7',
    Icon: Paintbrush,
  },
  {
    title: 'Complete Hands-Off Management',
    desc: 'We take care of everything — from tenant communication and check-ins to day-to-day property upkeep. You simply receive your rent.',
    span: 'md:col-span-12',
    Icon: ClipboardCheck,
  },
];

const REASONS = [
  {
    heading: 'Stress-Free Rental Income',
    body: 'No void periods, no missed payments, and no dealing with tenant issues.',
  },
  {
    heading: 'Professional Cleaning & Maintenance',
    body: 'Your property is professionally cleaned several times per week, keeping it in top condition.',
  },
  {
    heading: 'Secure & Vetted Tenants',
    body: 'We only accommodate professionals, corporate clients, families and trusted guests — all vetted.',
  },
  {
    heading: 'Full Compliance & Standards',
    body: 'We adhere to all legal requirements and keep the property in perfect condition.',
  },
  {
    heading: 'Long-Term Agreements for Stability',
    body: 'Secure 3–5+ year lease agreements with no interruptions.',
    span: 'sm:col-span-2',
  },
];

export default function Services() {
  return (
    <>
      <section id="what-we-do" className="theme-light t-bg2 py-28 lg:py-40" data-testid="services-section">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <SectionHeading label="What We Do" title="Everything handled, end to end" />
            <Reveal delay={0.2} className="lg:pb-2">
              <p className="t-muted max-w-sm text-sm font-light leading-relaxed">
                One agreement with us replaces the entire letting apparatus — agents, viewings, arrears and 2am phone
                calls.
              </p>
            </Reveal>
          </div>
          <div className="mt-16 grid grid-cols-1 gap-5 md:grid-cols-12">
            {SERVICES.map((s, i) => (
              <Reveal key={s.title} delay={0.08 * i} className={`col-span-1 ${s.span}`}>
                <div
                  data-testid={`service-card-${i}`}
                  className="group t-card t-border hover-lift flex h-full flex-col justify-between rounded-2xl border p-8 sm:p-10"
                >
                  <div className="flex items-start justify-between">
                    <span className="t-chip flex h-12 w-12 items-center justify-center rounded-full">
                      <s.Icon size={20} className="t-accent-text" />
                    </span>
                    <ArrowUpRight
                      size={18}
                      className="t-muted opacity-0 transition-all duration-300 group-hover:translate-x-1 group-hover:opacity-100 group-hover-accent"
                    />
                  </div>
                  <div className="mt-14">
                    <h3 className="font-display t-text text-xl font-semibold sm:text-2xl">{s.title}</h3>
                    <p className="t-text2 mt-3 max-w-lg text-sm font-light leading-relaxed">{s.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section id="why-us" className="theme-light t-bg py-28 lg:py-40" data-testid="why-us-section">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <SectionHeading label="Why Choose Us" title="Why landlords stay with The Gate Stays" />
          <div className="t-linebg t-border mt-16 grid grid-cols-1 gap-px overflow-hidden rounded-2xl border sm:grid-cols-2">
            {REASONS.map((r, i) => (
              <Reveal key={r.heading} delay={0.07 * i} className={r.span || ''}>
                <div
                  data-testid={`why-card-${i}`}
                  className="group t-bg h-full p-10 transition-colors duration-500 hover:bg-[var(--c-bg2)] sm:p-12"
                >
                  <p className="font-display t-num group-hover-num text-4xl font-medium transition-colors duration-500">
                    {String(i + 1).padStart(2, '0')}
                  </p>
                  <h3 className="font-display t-text mt-8 text-xl font-semibold sm:text-2xl">{r.heading}</h3>
                  <p className="t-text2 mt-3 max-w-lg text-sm font-light leading-relaxed">{r.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
